import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  fetchReminders,
  fetchReminderStats,
  completeReminder,
  deleteReminder,
} from '../../Redux/Slice/ReminderSlice';
import CreateReminderModal from '../../Components/Reminder/CreateReminderModal';
import SnoozeModal from '../../Components/Reminder/SnoozeModal';
import { toast } from 'react-toastify';
import {
  Bell,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Clock,
  Calendar,
  CheckCircle,
  AlertCircle,
  PhoneCall,
  ExternalLink,
  Edit2,
  Trash2,
  MoreVertical,
  Check,
  AlertTriangle,
  History,
  Tag,
  FileText,
  Hash,
  Users,
  User,
  Sparkles,
  Flame,
  Eye,
} from 'lucide-react';
import moment from 'moment';
import './ReminderManagement.css';

const BaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ReminderManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { reminders, stats, loading, pagination } = useSelector((state) => state.reminder);
  const { LoginData } = useSelector((state) => state.auth);
  const currentUserId = LoginData?.data?._id;

  // Team members from chat slice or API
  const { members } = useSelector((state) => state.chat || { members: [] });
  const [teamMembers, setTeamMembers] = useState(members || []);
  const [selectedMember, setSelectedMember] = useState('All');

  // Active filter states
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [page, setPage] = useState(1);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editReminderItem, setEditReminderItem] = useState(null);
  const [snoozeReminderItem, setSnoozeReminderItem] = useState(null);
  const [showSnoozeModal, setShowSnoozeModal] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Load team members for filter
  useEffect(() => {
    if (!members || members.length === 0) {
      axios
        .get(`${BaseUrl}/Chat/members`)
        .then((res) => {
          if (res.data?.data) {
            setTeamMembers(res.data.data);
          }
        })
        .catch((err) => console.error('Failed to load team members:', err));
    } else {
      setTeamMembers(members);
    }
  }, [members]);

  // Fetch reminders and statistics
  const loadData = useCallback(() => {
    dispatch(fetchReminderStats({ userId: currentUserId, scope: 'all' }));
    dispatch(
      fetchReminders({
        tab: activeTab,
        priority: selectedPriority !== 'All' ? selectedPriority : undefined,
        reminderType: selectedType !== 'All' ? selectedType : undefined,
        assignedUser: selectedMember !== 'All' ? selectedMember : undefined,
        search: searchQuery.trim() || undefined,
        userId: currentUserId,
        scope: 'all',
        page,
        limit: 50,
      })
    );
  }, [dispatch, activeTab, selectedPriority, selectedType, selectedMember, searchQuery, currentUserId, page]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle Complete
  const handleComplete = async (reminder) => {
    try {
      await dispatch(
        completeReminder({
          id: reminder._id,
          userId: currentUserId,
        })
      ).unwrap();
      toast.success('Reminder marked as completed!');
      loadData();
    } catch (err) {
      toast.error(err || 'Failed to complete reminder.');
    }
  };

  // Handle Delete
  const handleDelete = async (id) => {
    try {
      await dispatch(deleteReminder(id)).unwrap();
      toast.success('Reminder deleted.');
      setDeleteConfirmId(null);
      loadData();
    } catch (err) {
      toast.error(err || 'Failed to delete reminder.');
    }
  };

  // Format IST date and time
  const formatDateTime = (dateStr, timeStr) => {
    if (!dateStr) return '-';
    try {
      const m = moment(`${dateStr} ${timeStr || '00:00'}`, 'YYYY-MM-DD HH:mm');
      return m.format('DD MMM YYYY, hh:mm A');
    } catch (e) {
      return `${dateStr} ${timeStr || ''}`;
    }
  };

  const formatDateOnly = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return moment(dateStr).format('DD MMM YYYY');
    } catch (e) {
      return dateStr;
    }
  };

  const formatTimeOnly = (timeStr) => {
    if (!timeStr) return '-';
    try {
      return moment(`2000-01-01 ${timeStr}`).format('hh:mm A');
    } catch (e) {
      return timeStr;
    }
  };

  const formatTimestamp = (dateVal) => {
    if (!dateVal) return '-';
    try {
      return moment(dateVal).format('DD MMM YYYY, hh:mm A');
    } catch (e) {
      return String(dateVal);
    }
  };

  const isDateToday = (dateStr) => {
    if (!dateStr) return false;
    return moment(dateStr).isSame(moment(), 'day');
  };

  // Assigned Members Renderer
  const renderAssignedMembers = (reminder) => {
    if (reminder.assignMode === 'all') {
      return (
        <span className="all-team-badge" title="Assigned to All Support Team Members">
          <Users size={13} />
          All Support Team ({reminder.assignedTo?.length || teamMembers.length || 'All'})
        </span>
      );
    }

    const list = Array.isArray(reminder.assignedTo) ? reminder.assignedTo : [];
    if (list.length === 0) {
      return <span className="text-muted fst-italic" style={{ fontSize: '11.5px' }}>Not Assigned</span>;
    }

    return (
      <div className="d-flex flex-column gap-1">
        <div className="d-flex align-items-center flex-wrap gap-1">
          {list.slice(0, 2).map((member) => {
            const mName = member.Name || member.Email || 'Member';
            const initial = mName.charAt(0).toUpperCase();
            return (
              <div key={member._id} className="team-member-chip" title={`${mName} (${member.Role || 'Support'})`}>
                {member.ProfilePhoto ? (
                  <img
                    src={member.ProfilePhoto}
                    alt={mName}
                    className="team-avatar-xs"
                    style={{ objectFit: 'cover' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <span className="team-avatar-xs">{initial}</span>
                )}
                <span className="text-truncate" style={{ maxWidth: '105px' }}>{mName}</span>
              </div>
            );
          })}
          {list.length > 2 && (
            <span
              className="badge bg-light text-primary border rounded-pill px-2 py-1"
              style={{ fontSize: '10.5px', fontWeight: 600, cursor: 'default' }}
              title={list.map((m) => `${m.Name || m.Email} (${m.Role || 'Support'})`).join(', ')}
            >
              +{list.length - 2} more
            </span>
          )}
        </div>
      </div>
    );
  };

  // Priority Pill Helper
  const renderPriorityPill = (priority) => {
    const p = priority || 'Medium';
    const classMap = {
      Urgent: 'priority-pill-urgent',
      High: 'priority-pill-high',
      Medium: 'priority-pill-medium',
      Low: 'priority-pill-low',
    };
    return (
      <span className={`priority-pill ${classMap[p] || 'priority-pill-medium'}`}>
        <span
          className="rounded-circle"
          style={{
            width: '6px',
            height: '6px',
            backgroundColor: 'currentColor',
          }}
        />
        {p}
      </span>
    );
  };

  // Status Badge Helper
  const renderStatusBadge = (status, scheduledAt) => {
    const isPast = scheduledAt && new Date(scheduledAt) < new Date();
    let displayStatus = status || 'Pending';
    let className = 'status-pill-pending';

    if (displayStatus === 'Completed') {
      className = 'status-pill-completed';
    } else if (displayStatus === 'Snoozed') {
      className = 'status-pill-snoozed';
    } else if (displayStatus === 'Triggered') {
      className = 'status-pill-triggered';
    } else if (isPast) {
      displayStatus = 'Overdue';
      className = 'status-pill-overdue';
    }

    return <span className={`status-pill ${className}`}>{displayStatus}</span>;
  };

  return (
    <div className="reminder-management-container">
      {/* Top Header Card */}
      <div className="reminder-header-card">
        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <div
                className="d-flex align-items-center justify-content-center rounded-3 text-white"
                style={{
                  width: '36px',
                  height: '36px',
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                }}
              >
                <Bell size={20} />
              </div>
              <h4 className="fw-bold text-dark mb-0" style={{ letterSpacing: '-0.02em' }}>
                Reminder Management
              </h4>
            </div>
            <p className="text-secondary mb-0" style={{ fontSize: '13px' }}>
              Create, track, and manage all real-time follow-ups and support reminders
            </p>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center gap-1.5 rounded-3 py-2 px-3 fw-semibold bg-white border"
              style={{ fontSize: '13px' }}
              onClick={loadData}
              title="Refresh Reminders"
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>

            <button
              type="button"
              className="btn btn-primary d-flex align-items-center gap-2 rounded-3 py-2 px-3.5 fw-semibold shadow-sm"
              style={{
                fontSize: '13px',
                background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                border: 'none',
              }}
              onClick={() => {
                setEditReminderItem(null);
                setShowCreateModal(true);
              }}
            >
              <Plus size={16} />
              Add a Reminder
            </button>
          </div>
        </div>

        {/* KPI Stat Cards Row */}
        <div className="row g-3 mt-3">
          <div className="col-6 col-md-4 col-lg-2">
            <div className="reminder-stat-card">
              <div className="reminder-stat-icon-wrapper bg-primary bg-opacity-10 text-primary">
                <Bell size={22} />
              </div>
              <div>
                <div className="reminder-stat-val">{stats.total || 0}</div>
                <div className="reminder-stat-lbl">Total</div>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-4 col-lg-2">
            <div className="reminder-stat-card">
              <div className="reminder-stat-icon-wrapper bg-info bg-opacity-10 text-info">
                <Calendar size={22} />
              </div>
              <div>
                <div className="reminder-stat-val">{stats.upcoming || 0}</div>
                <div className="reminder-stat-lbl">Upcoming</div>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-4 col-lg-2">
            <div className="reminder-stat-card">
              <div className="reminder-stat-icon-wrapper bg-danger bg-opacity-10 text-danger">
                <Clock size={22} />
              </div>
              <div>
                <div className="reminder-stat-val">{stats.active || 0}</div>
                <div className="reminder-stat-lbl">Active / Due</div>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-4 col-lg-2">
            <div className="reminder-stat-card">
              <div className="reminder-stat-icon-wrapper bg-warning bg-opacity-10 text-warning">
                <History size={22} />
              </div>
              <div>
                <div className="reminder-stat-val">{stats.snoozed || 0}</div>
                <div className="reminder-stat-lbl">Snoozed</div>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-4 col-lg-2">
            <div className="reminder-stat-card">
              <div className="reminder-stat-icon-wrapper bg-secondary bg-opacity-10 text-danger">
                <AlertTriangle size={22} />
              </div>
              <div>
                <div className="reminder-stat-val">{stats.overdue || 0}</div>
                <div className="reminder-stat-lbl">Overdue</div>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-4 col-lg-2">
            <div className="reminder-stat-card">
              <div className="reminder-stat-icon-wrapper bg-success bg-opacity-10 text-success">
                <CheckCircle size={22} />
              </div>
              <div>
                <div className="reminder-stat-val">{stats.completed || 0}</div>
                <div className="reminder-stat-lbl">Completed</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Card: Tabs & Table */}
      <div className="reminder-table-card">
        {/* Navigation Tabs */}
        <div className="p-3 border-bottom">
          <div className="reminder-nav-tabs">
            <button
              type="button"
              className={`reminder-tab-btn ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('all');
                setPage(1);
              }}
            >
              All Reminders
              <span className="reminder-tab-badge">{stats.total || 0}</span>
            </button>

            <button
              type="button"
              className={`reminder-tab-btn ${activeTab === 'upcoming' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('upcoming');
                setPage(1);
              }}
            >
              Upcoming
              <span className="reminder-tab-badge">{stats.upcoming || 0}</span>
            </button>

            <button
              type="button"
              className={`reminder-tab-btn ${activeTab === 'active' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('active');
                setPage(1);
              }}
            >
              Active / Triggered
              <span className="reminder-tab-badge">{stats.active || 0}</span>
            </button>

            <button
              type="button"
              className={`reminder-tab-btn ${activeTab === 'snoozed' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('snoozed');
                setPage(1);
              }}
            >
              Snoozed
              <span className="reminder-tab-badge">{stats.snoozed || 0}</span>
            </button>

            <button
              type="button"
              className={`reminder-tab-btn ${activeTab === 'overdue' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('overdue');
                setPage(1);
              }}
            >
              Overdue
              <span className="reminder-tab-badge">{stats.overdue || 0}</span>
            </button>

            <button
              type="button"
              className={`reminder-tab-btn ${activeTab === 'completed' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('completed');
                setPage(1);
              }}
            >
              Completed
              <span className="reminder-tab-badge">{stats.completed || 0}</span>
            </button>
          </div>

          {/* Search & Filter Bar */}
          <div className="row g-2 mt-2 align-items-center">
            <div className="col-12 col-md-3">
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-light border-secondary border-opacity-25 text-muted">
                  <Search size={14} />
                </span>
                <input
                  type="text"
                  className="form-control border-secondary border-opacity-25"
                  placeholder="Search title, description, call #..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="btn btn-outline-secondary border-secondary border-opacity-25"
                    type="button"
                    onClick={() => setSearchQuery('')}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="col-6 col-md-3">
              <select
                className="form-select form-select-sm border-secondary border-opacity-25"
                value={selectedMember}
                onChange={(e) => setSelectedMember(e.target.value)}
              >
                <option value="All">All Team Members</option>
                {teamMembers.map((m) => (
                  <option key={m._id} value={m._id}>
                    {m.Name || m.Email} {m.Role ? `(${m.Role})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-6 col-md-3">
              <select
                className="form-select form-select-sm border-secondary border-opacity-25"
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
              >
                <option value="All">All Priorities</option>
                <option value="Urgent">Urgent Priority</option>
                <option value="High">High Priority</option>
                <option value="Medium">Medium Priority</option>
                <option value="Low">Low Priority</option>
              </select>
            </div>

            <div className="col-12 col-md-3">
              <select
                className="form-select form-select-sm border-secondary border-opacity-25"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
              >
                <option value="All">All Reminder Types</option>
                <optgroup label="Support & Client Operations">
                  <option value="Call Follow-up">Call Follow-up</option>
                  <option value="Support Ticket">Support Ticket</option>
                  <option value="Client Visit">Client Visit</option>
                  <option value="Client Training">Client Training</option>
                  <option value="Customer Query">Customer Query</option>
                </optgroup>
                <optgroup label="Development & Technical">
                  <option value="Bug / Error Fix">Bug / Error Fix</option>
                  <option value="Software Installation">Software Installation</option>
                  <option value="Module Update">Module Update</option>
                  <option value="Task Deadline">Task Deadline</option>
                  <option value="DB Sync & Backup">DB Sync & Backup</option>
                </optgroup>
                <optgroup label="Management & Accounts">
                  <option value="Payment / AMC Follow-up">Payment / AMC Follow-up</option>
                  <option value="Escalation">Escalation</option>
                  <option value="Team Meeting">Team Meeting</option>
                  <option value="General">General</option>
                </optgroup>
              </select>
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div className="table-responsive">
          <table className="reminder-table">
            <thead>
              <tr>
                <th style={{ minWidth: '95px', width: '100px' }}>Priority</th>
                <th style={{ minWidth: '220px', width: '25%' }}>Reminder Details</th>
                <th style={{ minWidth: '180px', width: '18%' }}>Assigned Team Member(s)</th>
                <th style={{ minWidth: '190px', width: '20%' }}>Linked Token & Customer</th>
                <th style={{ minWidth: '180px', width: '20%' }}>Scheduled & Alert History</th>
                <th style={{ minWidth: '110px', width: '115px', textAlign: 'center' }}>Status</th>
                <th style={{ minWidth: '135px', width: '140px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-5 text-muted">
                    <span className="spinner-border spinner-border-sm me-2 text-primary" role="status" />
                    Loading reminders...
                  </td>
                </tr>
              ) : reminders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-5">
                    <div className="d-flex flex-column align-items-center justify-content-center">
                      <div
                        className="rounded-circle bg-light d-flex align-items-center justify-content-center mb-2"
                        style={{ width: '56px', height: '56px' }}
                      >
                        <Bell size={26} className="text-muted" />
                      </div>
                      <div className="fw-bold text-dark" style={{ fontSize: '15px' }}>
                        No reminders found
                      </div>
                      <small className="text-muted mb-3" style={{ fontSize: '12px' }}>
                        {searchQuery
                          ? 'Try adjusting your search criteria or filter.'
                          : 'You are all caught up! Create a new reminder to stay organized.'}
                      </small>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary rounded-3 px-3 py-1.5 fw-semibold d-flex align-items-center gap-1.5"
                        onClick={() => {
                          setEditReminderItem(null);
                          setShowCreateModal(true);
                        }}
                      >
                        <Plus size={15} />
                        Add a Reminder
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                reminders.map((reminder) => {
                  const complaintObj =
                    typeof reminder.complaintId === 'object' && reminder.complaintId !== null
                      ? reminder.complaintId
                      : null;
                  const complaintIdStr =
                    complaintObj?._id || (typeof reminder.complaintId === 'string' ? reminder.complaintId : '');
                  const companyName =
                    typeof complaintObj?.CompanyName === 'object'
                      ? (complaintObj.CompanyName?.WhaClientName || '')
                      : typeof complaintObj?.CompanyName === 'string'
                      ? complaintObj.CompanyName
                      : '';
                  const isCompleted = reminder.status === 'Completed';

                  // Current user's alert view record
                  const myShown = reminder.shownHistory?.find(
                    (h) =>
                      h.userId?._id?.toString() === currentUserId?.toString() ||
                      h.userId?.toString() === currentUserId?.toString()
                  );
                  const shownCount = reminder.shownHistory?.length || 0;
                  const shownNamesTooltip = (reminder.shownHistory || [])
                    .map((h) => `${h.userId?.Name || h.userId?.Email || 'User'}: ${formatTimestamp(h.shownAt)}`)
                    .join('\n');

                  return (
                    <tr key={reminder._id}>
                      {/* Priority */}
                      <td>{renderPriorityPill(reminder.priority)}</td>

                      {/* Reminder Details */}
                      <td>
                        <div className="fw-bold text-dark mb-1" style={{ fontSize: '13.5px', letterSpacing: '-0.1px' }}>
                          {reminder.title}
                        </div>
                        {reminder.description && (
                          <div
                            className="text-secondary mb-2"
                            style={{ fontSize: '12px', lineHeight: '1.45', maxWidth: '340px' }}
                          >
                            {reminder.description}
                          </div>
                        )}
                        <div className="d-flex align-items-center flex-wrap gap-2">
                          <span className="badge rounded-pill bg-light text-secondary border d-inline-flex align-items-center gap-1 px-2 py-0.5" style={{ fontSize: '10.5px' }}>
                            <Tag size={10} className="text-muted" />
                            {reminder.reminderType || 'General'}
                          </span>
                          {reminder.createdBy?.Name && (
                            <small className="text-muted" style={{ fontSize: '11px' }}>
                              Created by: <strong className="text-dark">{reminder.createdBy.Name}</strong>
                            </small>
                          )}
                        </div>
                      </td>

                      {/* Assigned Team Member(s) */}
                      <td>
                        {renderAssignedMembers(reminder)}
                      </td>

                      {/* Linked Token & Customer Info */}
                      <td>
                        {reminder.callNo ? (
                          <div className="d-flex flex-column gap-1.5">
                            <div className="d-flex align-items-center gap-2">
                              {complaintIdStr ? (
                                <button
                                  type="button"
                                  className="call-badge-link border-0"
                                  onClick={() => navigate(`/ComplainDetailsIdSupport/${complaintIdStr}`)}
                                  title="Click to view support ticket details"
                                >
                                  <PhoneCall size={12} />
                                  Token #{reminder.callNo}
                                  <ExternalLink size={10} />
                                </button>
                              ) : (
                                <span className="call-badge-link">
                                  <PhoneCall size={12} />
                                  Token #{reminder.callNo}
                                </span>
                              )}

                              {complaintObj?.Status && (
                                <span
                                  className="badge rounded-pill fw-semibold px-2 py-0.5"
                                  style={{
                                    fontSize: '10.5px',
                                    backgroundColor: complaintObj.Status === 'Completed' ? '#ecfdf5' : '#eff6ff',
                                    color: complaintObj.Status === 'Completed' ? '#059669' : '#2563eb',
                                    border: `1px solid ${complaintObj.Status === 'Completed' ? '#a7f3d0' : '#bfdbfe'}`,
                                  }}
                                >
                                  {complaintObj.Status}
                                </span>
                              )}
                            </div>

                            {companyName && (
                              <div
                                className="fw-bold text-dark text-truncate"
                                style={{ fontSize: '12.5px', maxWidth: '300px' }}
                                title={companyName}
                              >
                                {companyName}
                              </div>
                            )}

                            {complaintObj?.Requirement && (
                              <div
                                className="text-secondary text-truncate"
                                style={{ fontSize: '11.5px', maxWidth: '300px' }}
                                title={complaintObj.Requirement}
                              >
                                {complaintObj.Requirement}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="badge bg-light text-muted border px-2 py-1" style={{ fontSize: '11px' }}>
                            No Token Linked
                          </span>
                        )}
                      </td>

                      {/* Scheduled & Alert History */}
                      <td>
                        <div className="d-flex flex-column gap-1.5">
                          {/* Scheduled Date/Time */}
                          <div className="d-flex align-items-center gap-2">
                            <div className="d-flex align-items-center gap-1.5 text-dark fw-semibold" style={{ fontSize: '12.5px' }}>
                              <Calendar size={13} className="text-primary" />
                              <span>{formatDateOnly(reminder.reminderDate)}</span>
                            </div>
                            <div className="d-flex align-items-center gap-1 text-secondary" style={{ fontSize: '12px' }}>
                              <Clock size={12} className="text-muted" />
                              <span>{formatTimeOnly(reminder.reminderTime)}</span>
                            </div>
                            {isDateToday(reminder.reminderDate) && (
                              <span className="badge bg-primary bg-opacity-10 text-primary ms-0.5 px-1.5 py-0.5" style={{ fontSize: '9.5px' }}>
                                Today
                              </span>
                            )}
                          </div>

                          {/* Alert Triggered Timestamp */}
                          {reminder.triggeredAt && (
                            <div className="triggered-alert-pill" title={`Alert triggered on ${formatTimestamp(reminder.triggeredAt)}`}>
                              <Flame size={11} />
                              <span>Alert Triggered: {formatTimestamp(reminder.triggeredAt)}</span>
                            </div>
                          )}

                          {/* Shown to User in Account */}
                          {myShown ? (
                            <div className="shown-alert-pill" title={`Shown to your account on ${formatTimestamp(myShown.shownAt)}`}>
                              <Sparkles size={11} />
                              <span>Shown in your A/C: {formatTimestamp(myShown.shownAt)}</span>
                            </div>
                          ) : reminder.status === 'Triggered' && !isCompleted ? (
                            <span className="text-muted fst-italic" style={{ fontSize: '10.5px' }}>
                              ⚡ Triggered in system
                            </span>
                          ) : null}

                          {/* Shown count to members */}
                          {shownCount > 0 && (
                            <small
                              className="text-muted d-inline-flex align-items-center gap-1"
                              style={{ fontSize: '10.5px', cursor: 'help' }}
                              title={shownNamesTooltip || `Shown to ${shownCount} team members`}
                            >
                              <Eye size={10} />
                              Shown to {shownCount} team member{shownCount > 1 ? 's' : ''}
                            </small>
                          )}

                          {/* Completed Details */}
                          {isCompleted && (
                            <div className="mt-1 pt-1 border-top border-light-subtle">
                              <div className="d-flex align-items-center gap-1 text-success fw-semibold" style={{ fontSize: '11px' }}>
                                <CheckCircle size={11} />
                                <span>Completed: {formatTimestamp(reminder.completedAt)}</span>
                              </div>
                              {reminder.completedBy?.Name && (
                                <small className="text-muted" style={{ fontSize: '10.5px' }}>
                                  By: <strong>{reminder.completedBy.Name}</strong>
                                </small>
                              )}
                              {reminder.completionNotes && (
                                <div
                                  className="text-secondary fst-italic text-truncate"
                                  style={{ fontSize: '10.5px', maxWidth: '210px' }}
                                  title={reminder.completionNotes}
                                >
                                  "{reminder.completionNotes}"
                                </div>
                              )}
                            </div>
                          )}

                          {/* Snooze Count */}
                          {reminder.status === 'Snoozed' && reminder.snoozeCount > 0 && (
                            <span className="badge bg-warning bg-opacity-15 text-warning-emphasis border border-warning-subtle align-self-start" style={{ fontSize: '10px' }}>
                              Snoozed {reminder.snoozeCount}x
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Status */}
                      <td style={{ textAlign: 'center' }}>
                        {renderStatusBadge(reminder.status, reminder.scheduledAt)}
                      </td>

                      {/* Actions */}
                      <td style={{ textAlign: 'center' }}>
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          {!isCompleted ? (
                            <>
                              {/* Complete Button */}
                              <button
                                type="button"
                                className="reminder-action-btn complete-btn"
                                onClick={() => handleComplete(reminder)}
                                title="Mark as Completed"
                              >
                                <Check size={16} />
                              </button>

                              {/* Snooze Button */}
                              <button
                                type="button"
                                className="reminder-action-btn snooze-btn"
                                onClick={() => {
                                  setSnoozeReminderItem(reminder);
                                  setShowSnoozeModal(true);
                                }}
                                title="Snooze Reminder"
                              >
                                <Clock size={15} />
                              </button>

                              {/* Edit Button */}
                              <button
                                type="button"
                                className="reminder-action-btn edit-btn"
                                onClick={() => {
                                  setEditReminderItem(reminder);
                                  setShowCreateModal(true);
                                }}
                                title="Edit Reminder"
                              >
                                <Edit2 size={14} />
                              </button>
                            </>
                          ) : (
                            <span className="badge rounded-pill bg-success bg-opacity-10 text-success fw-semibold px-2 py-1" style={{ fontSize: '11px' }}>
                              <CheckCircle size={11} className="me-1" />
                              Done
                            </span>
                          )}

                          {/* Delete Button */}
                          <button
                            type="button"
                            className="reminder-action-btn delete-btn"
                            onClick={() => setDeleteConfirmId(reminder._id)}
                            title="Delete Reminder"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit Modal */}
      {showCreateModal && (
        <CreateReminderModal
          show={showCreateModal}
          onHide={() => {
            setShowCreateModal(false);
            setEditReminderItem(null);
          }}
          editData={editReminderItem}
          onSuccess={loadData}
        />
      )}

      {/* Snooze Modal */}
      {showSnoozeModal && (
        <SnoozeModal
          reminder={snoozeReminderItem}
          show={showSnoozeModal}
          onHide={() => {
            setShowSnoozeModal(false);
            setSnoozeReminderItem(null);
          }}
          onSuccess={loadData}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(15, 23, 42, 0.65)', zIndex: 1065 }}
        >
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '380px' }}>
            <div className="modal-content border-0 shadow-lg rounded-4 p-4 text-center">
              <div
                className="rounded-circle bg-danger bg-opacity-10 text-danger mx-auto d-flex align-items-center justify-content-center mb-3"
                style={{ width: '50px', height: '50px' }}
              >
                <Trash2 size={24} />
              </div>
              <h6 className="fw-bold text-dark mb-1">Delete Reminder?</h6>
              <p className="text-secondary mb-4" style={{ fontSize: '13px' }}>
                Are you sure you want to delete this reminder? This action cannot be undone.
              </p>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-light flex-fill rounded-3 py-2 border fw-semibold"
                  style={{ fontSize: '13px' }}
                  onClick={() => setDeleteConfirmId(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger flex-fill rounded-3 py-2 fw-semibold"
                  style={{ fontSize: '13px' }}
                  onClick={() => handleDelete(deleteConfirmId)}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReminderManagement;
